import { useState } from 'react';
import { FaPaperPlane, FaUser, FaEnvelope, FaPhone, FaComment, FaWhatsapp, FaHome } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const ContactForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+91',
    phone: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      const numbersOnly = value.replace(/\D/g, '');
      setFormData(prev => ({ ...prev, phone: numbersOnly }));
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Name must be at most 100 characters';
    } else if (!/^[a-zA-Z\s'-]+$/.test(formData.name.trim())) {
      newErrors.name = 'Name can only contain letters, spaces, hyphens, and apostrophes';
    }
    
    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    } else if (formData.email.length > 255) {
      newErrors.email = 'Email must be at most 255 characters';
    }
    
    // Phone validation (optional)
    if (formData.phone) {
      const cleanedPhone = formData.phone.replace(/[\s-]/g, '');
      if (cleanedPhone.length !== 10) {
        newErrors.phone = 'Please enter a valid 10-digit phone number';
      } else if (!/^[6-9]\d{9}$/.test(cleanedPhone)) {
        newErrors.phone = 'Phone number must start with 6, 7, 8, or 9';
      }
    }
    
    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    } else if (formData.message.length > 2000) {
      newErrors.message = 'Message must be at most 2000 characters';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      // Sanitize input values before using in mailto link to prevent XSS
      const sanitizedName = formData.name.trim().substring(0, 100).replace(/[<>"']/g, '');
      const sanitizedEmail = formData.email.trim().toLowerCase().substring(0, 255);
      const sanitizedPhone = formData.phone.replace(/[^\d]/g, '').substring(0, 10);
      const sanitizedMessage = formData.message.trim().substring(0, 2000).replace(/[<>"']/g, '');
      const sanitizedCountryCode = formData.countryCode.replace(/[^\d+]/g, '').substring(0, 5);
      
      // Prepare the message content with sanitized values
      const messageContent = `
        Name: ${sanitizedName}
        Email: ${sanitizedEmail}
        Phone: ${sanitizedCountryCode} ${sanitizedPhone || 'Not provided'}
        Message: ${sanitizedMessage}
      `;

      // Send email (mailto link) - encodeURIComponent already handles URL encoding
      const emailSubject = `Contact Form Submission from ${sanitizedName}`;
      const emailBody = encodeURIComponent(messageContent);
      const mailtoLink = `mailto:yogvital@gmail.com?subject=${encodeURIComponent(emailSubject)}&body=${emailBody}`;
      
      // Open email client and wait for it to complete
      const emailWindow = window.open(mailtoLink);
      
      // Only show success after email client is opened
      if (emailWindow) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        setSubmitSuccess(true);
        setFormData({ 
          name: '', 
          email: '', 
          countryCode: '+91', 
          phone: '', 
          message: '' 
        });
      }
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openWhatsAppChat = () => {
    const messageContent = `Hi Yogvital, I have a question`;
    window.open(`https://wa.me/918923162316?text=${encodeURIComponent(messageContent)}`);
  };

  if (submitSuccess) {
    return (
      <div className="max-w-md mx-auto p-8 bg-white rounded-xl shadow-lg text-center">
        <div className="text-green-500 text-5xl mb-4">✓</div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Thank You!</h2>
        <p className="text-gray-600 mb-6">
          Your message has been sent successfully. We'll get back to you soon.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => setSubmitSuccess(false)}
            className="flex items-center justify-center gap-2 bg-accent text-white px-6 py-2 rounded-lg hover:bg-accent-dark transition-colors transform hover:scale-105 active:scale-95"
          >
            <FaPaperPlane className="h-4 w-4" />
            Send Another Message
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex items-center justify-center gap-2 bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors transform hover:scale-105 active:scale-95"
          >
            <FaHome className="h-4 w-4" />
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto bg-white rounded-xl shadow-md overflow-hidden md:max-w-2xl">
        <div className="p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Contact Us</h1>
            <p className="text-gray-600">
              Have questions? Fill out the form below and we'll get back to you soon.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            {/* Name Field */}
            <div className="relative">
              <label htmlFor="contact-name" className="sr-only">
                Your Name
                <span className="text-red-500 ml-1" aria-label="required">*</span>
              </label>
              <div className="absolute top-3 left-3 flex items-center h-[calc(100%-24px)] pointer-events-none" aria-hidden="true">
                <FaUser className={`h-5 w-5 ${errors.name ? 'text-red-500' : 'text-gray-400'}`} />
              </div>
              <input
                type="text"
                id="contact-name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your Name"
                className={`pl-10 w-full px-4 py-3 rounded-lg border ${errors.name ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent`}
                aria-required="true"
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? "name-error" : undefined}
                autoComplete="name"
              />
              {errors.name && (
                <p 
                  id="name-error"
                  className="mt-1 text-sm text-red-500" 
                  role="alert"
                  aria-live="polite"
                >
                  {errors.name}
                </p>
              )}
            </div>

            {/* Email Field */}
            <div className="relative">
              <label htmlFor="contact-email" className="sr-only">
                Your Email
                <span className="text-red-500 ml-1" aria-label="required">*</span>
              </label>
              <div className="absolute top-3 left-3 flex items-center h-[calc(100%-24px)] pointer-events-none" aria-hidden="true">
                <FaEnvelope className={`h-5 w-5 ${errors.email ? 'text-red-500' : 'text-gray-400'}`} />
              </div>
              <input
                type="email"
                id="contact-email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Your Email"
                className={`pl-10 w-full px-4 py-3 rounded-lg border ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent`}
                aria-required="true"
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? "email-error" : undefined}
                autoComplete="email"
              />
              {errors.email && (
                <p 
                  id="email-error"
                  className="mt-1 text-sm text-red-500" 
                  role="alert"
                  aria-live="polite"
                >
                  {errors.email}
                </p>
              )}
            </div>

            {/* Phone Field */}
            <div className="relative">
              <label htmlFor="contact-phone" className="sr-only">
                Phone Number (Optional)
              </label>
              <div className="flex items-center w-full">
                <div className="relative flex items-center">
                  <div className="absolute left-3 z-10 pointer-events-none" aria-hidden="true">
                    <FaPhone className={`h-5 w-5 ${errors.phone ? 'text-red-500' : 'text-gray-400'}`} />
                  </div>
                  <input
                    type="text"
                    name="countryCode"
                    value={formData.countryCode}
                    onChange={handleChange}
                    className="w-20 pl-10 pr-3 py-3 rounded-l-lg border border-r-0 border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent bg-gray-50"
                    aria-label="Country code"
                    aria-hidden="true"
                    tabIndex={-1}
                  />
                </div>
                <input
                  type="tel"
                  id="contact-phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Phone Number (Optional)"
                  className={`flex-1 pl-3 pr-4 py-3 rounded-r-lg border ${errors.phone ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent`}
                  maxLength="10"
                  aria-invalid={!!errors.phone}
                  aria-describedby={errors.phone ? "phone-error" : undefined}
                  autoComplete="tel"
                />
              </div>
              {errors.phone && (
                <p 
                  id="phone-error"
                  className="mt-1 text-sm text-red-500" 
                  role="alert"
                  aria-live="polite"
                >
                  {errors.phone}
                </p>
              )}
            </div>

            {/* Message Field */}
            <div className="relative">
              <label htmlFor="contact-message" className="sr-only">
                Your Message
                <span className="text-red-500 ml-1" aria-label="required">*</span>
              </label>
              <div className="absolute top-4 left-3 pointer-events-none" aria-hidden="true">
                <FaComment className={`h-5 w-5 ${errors.message ? 'text-red-500' : 'text-gray-400'}`} />
              </div>
              <textarea
                id="contact-message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Your Message"
                rows="5"
                className={`pl-10 w-full px-4 py-3 rounded-lg border ${errors.message ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent`}
                aria-required="true"
                aria-invalid={!!errors.message}
                aria-describedby={errors.message ? "message-error" : undefined}
              ></textarea>
              {errors.message && (
                <p 
                  id="message-error"
                  className="mt-1 text-sm text-red-500" 
                  role="alert"
                  aria-live="polite"
                >
                  {errors.message}
                </p>
              )}
            </div>

            {/* Buttons */}
            <div className="space-y-4">
              <button
                type="submit"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
                aria-label={isSubmitting ? 'Sending message, please wait' : 'Send message'}
                className={`w-full flex justify-center items-center gap-2 py-3 px-4 bg-accent hover:bg-accent-dark text-white font-medium rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-lg transform hover:-translate-y-1 active:translate-y-0'}`}
              >
                {isSubmitting ? (
                  <>
                    <svg 
                      className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" 
                      xmlns="http://www.w3.org/2000/svg" 
                      fill="none" 
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <FaPaperPlane className="h-4 w-4" aria-hidden="true" />
                    <span>Send Message</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={openWhatsAppChat}
                aria-label="Open WhatsApp chat"
                className="w-full flex justify-center items-center gap-2 py-3 px-4 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1 active:translate-y-0 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                <FaWhatsapp className="h-4 w-4" aria-hidden="true" />
                <span>Chat on WhatsApp</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;